// components/LineChartBySubBrand.jsx
import { Card, Box, Typography } from "@mui/material";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    CartesianGrid
} from "recharts";





export default function LineChartSubBrand({
    title,
    series,
    xLabels,
    dataMatrix,
    yTicks,
    yAxisLabel = "Cases Ordered (M)",
    valueFormatter = (v) => `${v}M`
}) {

    // Build chartData using ACTUAL + FORECAST from server JSON
    const chartData = xLabels?.map((label, i) => {
        const row = { month: label };

        series.forEach((sb, sIndex) => {
            // actual data
            row[sb.label] = dataMatrix?.actual?.[i]?.[sIndex] ?? 0;

            // forecast only if exists
            if (dataMatrix?.forecast) {
                row[sb.label + "_forecast"] =
                    dataMatrix.forecast?.[i]?.[sIndex] ?? null;
            }
        });

        return row;
    });


    //  Your CustomTooltip (unchanged)
    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload) return null;
        return (
            <Box
                sx={{
                    maxWidth: 300,
                    maxHeight: 200,
                    overflowY: "auto",
                    bgcolor: "#fff",
                    borderRadius: "8px",
                    boxShadow: "0px 3px 12px rgba(0,0,0,0.2)",
                    p: 1.5,
                    border: "1px solid #ddd",
                    zIndex: 999999
                }}
            >
                <Typography sx={{ fontWeight: 600, mb: 1 }}>{label}</Typography>

                {payload.map((item, idx) => (
                    <Box
                        key={idx}
                        sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
                    >
                        <span style={{ color: item.color, fontWeight: 600 }}>
                            {item.name}
                        </span>
                        <span style={{ color: "#333" }}>{valueFormatter(item.value)}</span>
                    </Box>
                ))}
            </Box>
        );
    };

    const chartWidth = Math.max(chartData?.length * 90, 900);
    return (
        <Card
            sx={{
                width: "100%",
                borderRadius: "12px !important",
                background: "#fff",
                overflow: "hidden",
                boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.1)",
                position: "relative",
                overflow: "visible",
                height: "100%"
            }}
        >
            {/* TITLE BAR — unchanged */}
            <Box
                sx={{
                    position: "absolute",
                    top: "-10px",
                    left: "12px",
                    right: "12px",
                    background: "#115f9c",
                    height: "36px",
                    borderRadius: "4px !important",
                    display: "flex",
                    alignItems: "center",
                    px: "12px",
                    color: "#fff",
                    fontSize: { xs: 12, sm: 12, md: 16 },
                    fontWeight: 200,
                    boxShadow: "0px 1px 16px rgba(128, 203, 255, 0.5)"
                }}
            >
                {title}
            </Box>

            <Box sx={{ width: "100%", mt: 2 }}>
                <Box
                    sx={{
                        width: "100%",
                        height: 400,
                        p: 2,
                        overflowX: "auto",
                        overflowY: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            width: {
                                xs: chartWidth,   //  enable horizontal scroll on mobile
                                sm: chartWidth,   // enable horizontal scroll on tablet
                                md: chartWidth,
                                lg: "100%"
                            },
                            minWidth: {
                                xs: chartWidth,
                                sm: chartWidth,
                                md: chartWidth,
                                lg: "100%"
                            },
                            height: "100%",
                        }}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={chartData}
                                margin={{ top: 30, right: 40, left: 10, bottom: 10 }}
                            >
                                <CartesianGrid stroke="#1111110D" vertical={false} />

                                <YAxis
                                    width={55}
                                    domain={[0, yTicks?.[yTicks?.length - 1]]}   // max tick = last element
                                    ticks={yTicks}
                                    tick={{ fontSize: 10, fill: "#11111180", dx: -12, fontWeight: 200 }} // move labels left
                                    axisLine={false}
                                    tickLine={false}
                                    label={{
                                        value: yAxisLabel,
                                        angle: -90,
                                        position: "insideLeft",
                                        dx: 5,
                                        dy: 40,
                                        style: { fontSize: 10, fill: "#111111", fontWeight: 200 }
                                    }}
                                />

                                <XAxis
                                    dataKey="month"
                                    interval={0}
                                    height={35}
                                    tick={{ fontSize: 10, color: "#111111", fontWeight: 200, }}
                                    tickMargin={10}
                                    padding={{ left: 25, right: 10 }}
                                />

                                <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 999999 }} />

                                {/* SOLID LINES (Actual values) */}
                                {series.map(sb => (
                                    <Line
                                        key={sb.label}
                                        type="linear"
                                        dataKey={sb.label}
                                        stroke={sb.color}
                                        strokeWidth={2}
                                        dot={{
                                            r: 4,
                                            fill: sb.color,
                                            stroke: sb.color
                                        }}
                                        activeDot={{
                                            r: 5,
                                            fill: sb.color,
                                            stroke: sb.color
                                        }}
                                        isAnimationActive={false}
                                    />
                                ))}

                                {/* DOTTED FORECAST LINES — only if forecast exists */}
                                {dataMatrix.forecast &&
                                    series.map(sb => (
                                        <Line
                                            key={sb.label + "_forecast"}
                                            type="linear"
                                            dataKey={sb.label + "_forecast"}
                                            stroke={sb.color}
                                            strokeWidth={1.6}
                                            strokeDasharray="7 4"
                                            dot={false}
                                            isAnimationActive={false}
                                        />
                                    ))
                                }

                            </LineChart>
                        </ResponsiveContainer>
                    </Box>

                </Box>

                {/* Legend */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        m: '16px'
                    }}
                >
                    {series.map((s) => (
                        <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    backgroundColor: s.color
                                }}
                            />
                            <Typography fontSize={10} style={{ color: "#111111", fontWeight: 200 }}>{s.label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Card>
    );
}
